import { styled } from '@mui/material/styles';
import { Box, Container, Card, Typography } from '@mui/material';
import { BRAND_COLORS, SEMANTIC_COLORS, alpha } from '../../styles/theme';

export const MainWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: `${alpha(BRAND_COLORS.forestGreen.main, 0.05)} !important`,
  width: '100%',
  position: 'relative',
});

export const Header = styled(Box)({
  backgroundColor: `${BRAND_COLORS.white.main} !important`,
  padding: '1rem 0',
  zIndex: 10,
  borderBottom: `1px solid ${BRAND_COLORS.yellow.main}`,
  width: '100%',
  position: 'sticky',
  top: 0,
});

export const HeaderContainer = styled(Box)({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 1.5rem',
});

export const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  }
}));

export const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
});

export const LogoAndTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
});

export const MaintenanceTitle = styled(Typography)({
  fontWeight: 700,
  color: BRAND_COLORS.forestGreen.main,
  display: 'inline-block',
  borderBottom: `3px solid ${BRAND_COLORS.yellow.main}`,
  paddingBottom: '0.25rem',
  lineHeight: 1.2,
  fontSize: '2rem',
});

export const UserInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
});

export const UserName = styled(Typography)({
  fontWeight: 600,
  color: BRAND_COLORS.forestGreen.main,
  fontSize: '0.875rem',
});

export const UserRole = styled(Typography)({
  fontSize: '0.75rem',
  color: alpha(BRAND_COLORS.forestGreen.main, 0.7),
  letterSpacing: '0.5px',
  fontWeight: 500,
});

export const AuthStatus = styled(Typography)({
  color: '#d32f2f', // error color
  fontSize: '0.875rem',
  fontWeight: 500,
});

export const MainContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  padding: '0 1.5rem',
  [theme.breakpoints.down('sm')]: {
    padding: '0 1rem',
  }
}));

export const TopSectionWrapper = styled(Box)({
  marginBottom: '24px',
});

export const FilterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '24px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    gap: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  }
}));

export const FilterBox = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '200px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  }
}));

export const StatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)', // Fixed 8 columns for 8 stats cards
  gap: '8px',
  marginBottom: '24px',
  [theme.breakpoints.down('xl')]: {
    gap: '6px',
  },
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)', // 4 columns on large screens
    gap: '8px',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns on medium screens
    gap: '8px',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns on small screens
    gap: '8px',
  }
}));

export const StatsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  }
}));

export const StatBox = styled(Box)(({ theme }) => ({
  padding: '20px',
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
  borderRadius: '8px',
  border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  }
}));

export const StatCard = styled(Card)(({ theme }) => ({
  flex: 1,
  backgroundColor: BRAND_COLORS.white.main,
  border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.1)}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  borderRadius: '8px',
  padding: '16px',
  position: 'relative',
  overflow: 'visible',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  [theme.breakpoints.down('md')]: {
    padding: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  }
}));

export const DateSliderSection = styled(Box)({
  padding: '20px',
  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.05),
  borderRadius: '8px',
  border: `1px solid ${alpha(BRAND_COLORS.yellow.main, 0.2)}`,
});

export const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: BRAND_COLORS.forestGreen.main,
  lineHeight: 1.2,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  }
}));

export const StatLabel = styled(Typography)({
  fontSize: '0.875rem',
  color: SEMANTIC_COLORS.text.secondary,
  marginBottom: '4px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

export const StatSubtext = styled(Typography)({
  fontSize: '0.75rem',
  color: alpha(SEMANTIC_COLORS.text.secondary, 0.7),
  marginTop: '4px',
});

export const DroneStatsGrid = styled(Box)({
  marginTop: '24px',
});

export const TabContent = styled(Box)({
  marginTop: '24px',
  minHeight: '400px',
});
